import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FIELD_COLORS = {
  pest365: '#2563eb',
  pest365Plus: '#8b5cf6',
  oneTime: '#f59e0b',
  initialServiceVisit: '#16a34a',
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
};

const TREND_CONFIG = {
  increasing: { label: 'Increasing', arrow: '\u2191', color: '#16a34a' },
  decreasing: { label: 'Decreasing', arrow: '\u2193', color: '#dc2626' },
  stable: { label: 'Stable', arrow: '\u2192', color: '#6b7280' },
};

function getGaugeColor(rate) {
  if (rate > 0.9) return '#16a34a';
  if (rate >= 0.7) return '#f59e0b';
  return '#dc2626';
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: '4px 0 20px 0',
  },
  topSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    marginBottom: 28,
  },
  gaugeContainer: {
    position: 'relative',
    width: 160,
    height: 160,
    flexShrink: 0,
  },
  gaugeLabel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  gaugePct: {
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1,
  },
  gaugeCaption: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },
  trendBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 600,
  },
  chartContainer: {
    position: 'relative',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#374151',
  },
  legendSwatch: {
    width: 12,
    height: 3,
    borderRadius: 2,
  },
  legendSwatchArea: {
    width: 12,
    height: 8,
    borderRadius: 2,
    opacity: 0.3,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.6,
    zIndex: 9999,
    minWidth: 180,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: 14,
  },
};

export default function PriceStickinessGauge({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const hasData = useMemo(() => {
    return (
      data &&
      Array.isArray(data.dates) &&
      data.dates.length > 0 &&
      Array.isArray(data.stickinessRate) &&
      data.stickinessRate.length > 0
    );
  }, [data]);

  const gaugeColor = useMemo(() => {
    if (!data || data.overall == null) return '#9ca3af';
    return getGaugeColor(data.overall);
  }, [data]);

  const trendInfo = useMemo(() => {
    if (!data || !data.trend) return TREND_CONFIG.stable;
    return TREND_CONFIG[data.trend] || TREND_CONFIG.stable;
  }, [data]);

  // Render the D3 line chart
  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 200;
    const margin = { top: 12, right: 16, bottom: 28, left: 44 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const parseDate = d3.timeParse('%Y-%m-%d');
    const dates = data.dates.map((d) => parseDate(d));

    const xScale = d3.scaleTime().domain(d3.extent(dates)).range([0, innerW]);

    const allValues = [...data.stickinessRate];
    if (data.byField) {
      Object.values(data.byField).forEach((arr) => {
        if (Array.isArray(arr)) allValues.push(...arr);
      });
    }
    const yMin = Math.min(d3.min(allValues) || 0, 0.5);
    const yScale = d3
      .scaleLinear()
      .domain([yMin, 1])
      .range([innerH, 0])
      .nice();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(yScale.ticks(5))
      .join('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Axes
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(Math.min(dates.length, 6))
          .tickFormat(d3.timeFormat('%b %d'))
      )
      .call((sel) => sel.select('.domain').attr('stroke', '#d1d5db'))
      .call((sel) => sel.selectAll('.tick line').attr('stroke', '#d1d5db'))
      .call((sel) =>
        sel
          .selectAll('.tick text')
          .attr('fill', '#6b7280')
          .attr('font-size', 10)
      );

    g.append('g')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickFormat((d) => `${Math.round(d * 100)}%`)
      )
      .call((sel) => sel.select('.domain').attr('stroke', '#d1d5db'))
      .call((sel) => sel.selectAll('.tick line').attr('stroke', '#d1d5db'))
      .call((sel) =>
        sel
          .selectAll('.tick text')
          .attr('fill', '#6b7280')
          .attr('font-size', 10)
      );

    // Filled area for overall stickiness
    const areaGen = d3
      .area()
      .x((_, i) => xScale(dates[i]))
      .y0(innerH)
      .y1((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.stickinessRate)
      .attr('d', areaGen)
      .attr('fill', '#3b82f6')
      .attr('fill-opacity', 0.12);

    // Overall stickiness line
    const lineGen = d3
      .line()
      .x((_, i) => xScale(dates[i]))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.stickinessRate)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2.5);

    // Per-field lines
    if (data.byField) {
      Object.entries(data.byField).forEach(([field, values]) => {
        if (!Array.isArray(values) || !FIELD_COLORS[field]) return;

        const fieldLine = d3
          .line()
          .x((_, i) => xScale(dates[i]))
          .y((d) => yScale(d))
          .defined((d) => d != null)
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(values)
          .attr('d', fieldLine)
          .attr('fill', 'none')
          .attr('stroke', FIELD_COLORS[field])
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.7);
      });
    }

    // Hover overlay for tooltips
    const bisect = d3.bisector((_, i) => dates[i]).left;

    const overlay = g
      .append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

    const hoverLine = g
      .append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('display', 'none');

    const hoverDot = g
      .append('circle')
      .attr('r', 4)
      .attr('fill', '#3b82f6')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('display', 'none');

    overlay
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event, this);
        const x0 = xScale.invert(mx);
        let idx = d3.bisectLeft(dates, x0);
        if (idx >= dates.length) idx = dates.length - 1;
        if (idx > 0) {
          const d0 = dates[idx - 1];
          const d1 = dates[idx];
          if (x0 - d0 < d1 - x0) idx = idx - 1;
        }

        const cx = xScale(dates[idx]);
        const cy = yScale(data.stickinessRate[idx]);

        hoverLine.attr('x1', cx).attr('x2', cx).attr('display', null);
        hoverDot.attr('cx', cx).attr('cy', cy).attr('display', null);

        const fieldLines = {};
        if (data.byField) {
          Object.entries(data.byField).forEach(([field, values]) => {
            if (Array.isArray(values) && values[idx] != null) {
              fieldLines[field] = values[idx];
            }
          });
        }

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: data.dates[idx],
          overall: data.stickinessRate[idx],
          fields: fieldLines,
        });
      })
      .on('mouseleave', function () {
        hoverLine.attr('display', 'none');
        hoverDot.attr('display', 'none');
        setTooltip(null);
      });
  }, [hasData, data]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Stickiness Monitor</h3>
        <p style={styles.subtitle}>
          Percentage of addresses with unchanged prices between scrapes
        </p>
        <div style={styles.emptyState}>No stickiness data available.</div>
      </div>
    );
  }

  const pctDisplay = Math.round((data.overall || 0) * 100);

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Stickiness Monitor</h3>
      <p style={styles.subtitle}>
        Percentage of addresses with unchanged prices between scrapes
      </p>

      {/* Top section: Gauge + Trend badge */}
      <div style={styles.topSection}>
        <div style={styles.gaugeContainer}>
          <svg viewBox="0 0 160 160" width={160} height={160}>
            {/* Background arc */}
            <path
              d={describeArc(80, 80, 62, -135, 135)}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={14}
              strokeLinecap="round"
            />
            {/* Filled arc */}
            <path
              d={describeArc(
                80,
                80,
                62,
                -135,
                -135 + 270 * Math.min(data.overall || 0, 1)
              )}
              fill="none"
              stroke={gaugeColor}
              strokeWidth={14}
              strokeLinecap="round"
            />
          </svg>
          <div style={styles.gaugeLabel}>
            <div style={{ ...styles.gaugePct, color: gaugeColor }}>
              {pctDisplay}%
            </div>
            <div style={styles.gaugeCaption}>stickiness</div>
          </div>
        </div>

        <div
          style={{
            ...styles.trendBadge,
            background: `${trendInfo.color}14`,
            color: trendInfo.color,
          }}
        >
          <span style={{ fontSize: 18 }}>{trendInfo.arrow}</span>
          {trendInfo.label}
        </div>
      </div>

      {/* Bottom section: D3 chart */}
      <div style={styles.chartContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 200"
          width="100%"
          style={{ display: 'block' }}
        />

        {/* Tooltip */}
        {tooltip && (
          <div
            style={{
              ...styles.tooltip,
              left: tooltip.x,
              top: tooltip.y,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {tooltip.date}
            </div>
            <div>
              Overall:{' '}
              <span style={{ color: '#93c5fd' }}>
                {(tooltip.overall * 100).toFixed(1)}%
              </span>
            </div>
            {Object.entries(tooltip.fields).map(([field, val]) => (
              <div key={field}>
                {FIELD_LABELS[field] || field}:{' '}
                <span style={{ color: FIELD_COLORS[field] || '#fff' }}>
                  {(val * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div
            style={{
              ...styles.legendSwatchArea,
              background: '#3b82f6',
            }}
          />
          <span>Overall Stickiness</span>
        </div>
        {Object.entries(FIELD_COLORS).map(([field, color]) => (
          <div key={field} style={styles.legendItem}>
            <div style={{ ...styles.legendSwatch, background: color }} />
            <span>{FIELD_LABELS[field]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Compute an SVG arc path for a circular gauge.
 * Center (cx, cy), radius r, from startAngle to endAngle in degrees.
 * 0 degrees = top (12 o'clock).
 */
function describeArc(cx, cy, r, startAngle, endAngle) {
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}
